<script setup>
import {
  inject,
  ref,
  computed,
  onBeforeUnmount,
  watch,
  nextTick,
  onMounted,
  onServerPrefetch
} from 'vue'
import { useRoute } from 'vue-router'
import { TvbSsoButton } from '@tvb-sz/tvb-headerfooter-frontend'
import { postArticleLike } from '@/api/article.js'
import { getAds } from '@/api/app'
import {
  toISOString,
  getTvbPlayerType,
  buildInstreamAdTagUrl,
  getInstreamAdSize,
  appendUtmParams
} from '@/utils'
import { pageview, action, popUp } from '@/utils/gtm'
import {
  useIntersectionObserver,
  useElementBounding,
  useElementVisibility,
  useElementSize
} from '@vueuse/core'
import { useAppStore } from '@/stores/app'
import { useHead } from '@unhead/vue'
import { sha1 } from 'js-sha1'
import useArticle from './useArticle'
import ArticleContent from '../ArticleContent/index.vue'
import Relation from '../Relation/index.vue'
import Subtitle from '../Subtitle/index.vue'
import leftTool from '../ArticleTool/leftTool.vue'
import bottomTool from '../ArticleTool/bottomTool.vue'
import TvbPlayer from './../../../../../components/TvbPlayer/index.vue'
import TvbDraggable from './../../../../../components/TvbDraggable/index.vue'
import TvbAds from '@/components/TvbAds/index.vue'
import { contentCopyHandler } from '@/utils/copyright'
import { Head } from '@unhead/vue/components'

const emits = defineEmits(['play'])

const props = defineProps({
  info: {
    type: Object,
    default() {
      return {}
    }
  },
  // 文章索引，用于判断是否第一篇文章
  index: {
    type: Number,
    default: 0
  }
})

// 是否为第一篇文章（用于控制 topbn 广告仅在第一篇显示）
const isFirstArticle = computed(() => props.index === 0)

const { config } = useAppStore()
const route = useRoute()
const isPreview = inject('isPreview')
const size = inject('size')
const width = inject('width')
const setAdParams = inject('setAdParams')
const activeArticle = inject('activeArticle')
const seoTitleSuffix = inject('seoTitleSuffix')
// 广告开关：1=显示广告，2=隐藏所有广告（默认显示）
const isShowAd = inject('isShowAd', ref(1))
const loginRef = ref()

const articleRef = ref(null)
const draggableRef = ref(null)
const tvbPlayerRef = ref(null)
const isPlay = ref(false)
const isMobile = computed(() => ['s', 'xs'].includes(size.value))
const showRelation = computed(() => ['xl'].includes(size.value))
const articleId = computed(() => props.info.id)
const isActive = computed(() => activeArticle.value?.id === articleId.value)
const author = computed(() => props.info.author?.join('、'))
const draggableDisabled = ref(true)
const elementBounding = useElementBounding(articleRef)

const {
  delayShow,
  breadcrumbs,
  categoryInfo,
  cloading,
  eloading,
  fetchCategoryInfo,
  fetchExtraInfo
} = useArticle()

const onStateChange = ({ state }) => {
  isPlay.value = state === 'play'
  if (isPlay.value) emits('play')
  if (state === 'ended') draggableDisabled.value = true
}

const pause = () => {
  tvbPlayerRef?.value?.pause()
  draggableDisabled.value = true
}

const play = () => {
  setTimeout(() => {
    tvbPlayerRef?.value?.play()
    draggableDisabled.value = true
  }, 1000)
}

const draggableIntersectionObserver = useIntersectionObserver(
  draggableRef,
  ([{ isIntersecting }]) => {
    draggableDisabled.value = isIntersecting || !isPlay.value
  }
)

// 面包屑
fetchCategoryInfo(props.info.category_id)

const is_like = ref(false)
const like_num = ref(0)
const contents = ref()
const articleVideo = ref({
  video_type: 0,
  video_id: 0,
  video_url: '',
  content_id: 0,
  region_only: 0
})
const extraCallback = (data) => {
  is_like.value = data?.is_like
  like_num.value = data?.like_num || 0
  contents.value = data?.contents
  articleVideo.value = data?.video
  // 视频信息加载完成后，获取 Instream 广告配置
  fetchInstreamAdConfig()
}
const extraInfoParams = { content_type: props.info.content_type }
if (isPreview.value) {
  extraInfoParams['key'] = route?.params?.articleId
}

onMounted(() => {
  fetchExtraInfo(articleId.value, extraInfoParams, extraCallback)
})

onServerPrefetch(async () => {
  await fetchExtraInfo(articleId.value, extraInfoParams, extraCallback)
})

// ========== Instream 广告逻辑 ==========
// Instream 广告标签 URL（VAST）
const articleAdTagUrl = ref('')
// 当前播放的广告类型，用于区分 pr/mr 的 onAdEnd 处理
const currentAdType = ref('pr')
// 是否启用 Instream 广告
const showInstreamAds = computed(() => {
  return !!(isShowAd.value !== 2 && articleVideo.value?.video_url && articleAdTagUrl.value)
})

// 获取 pr Instream 广告配置并构造 VAST URL
const fetchInstreamAdConfig = async () => {
  if (isShowAd.value === 2) return
  try {
    const { data } = await getAds({ position: 9, adtype: 'pr' })
    if (data?.ad_unit_prefix) {
      currentAdType.value = 'pr' // 重置广告类型，确保切换文章后从 pr 开始
      // 从接口返回的 size_setting 中根据屏幕尺寸匹配广告尺寸
      const adSize = getInstreamAdSize(data.ads_list, 'pr')
      articleAdTagUrl.value = buildInstreamAdTagUrl({
        adUnitPrefix: data.ad_unit_prefix,
        adChildUnit: 'pr',
        adSize,
        customParams: {
          adtype: 'vod',
          vtype: 'vod',
          content_type: 'content',
          sub_category_path: categoryInfo?.value?.path || 'x',
          article_id: articleId.value || 'x',
          video_id: articleId.value || 'x'
        },
        // 视频时长（秒），GAM 依据此参数返回 mid-roll 广告（TC-1596）
        videoDuration: articleVideo.value?.video_time || 0
      })
    }
  } catch (error) {
    console.error('Failed to fetch instream ad config:', error)
  }
}

// pr 广告结束后请求 mr 广告（SDK 自动在 mid-roll 时间点播放）
const fetchMrAdConfig = async () => {
  if (isShowAd.value === 2) return
  try {
    const { data } = await getAds({ position: 9, adtype: 'mr' })
    if (data?.ad_unit_prefix) {
      const adSize = getInstreamAdSize(data.ads_list, 'mr')
      const mrAdTagUrl = buildInstreamAdTagUrl({
        adUnitPrefix: data.ad_unit_prefix,
        adChildUnit: 'mr',
        adSize,
        customParams: {
          adtype: 'vod',
          vtype: 'vod',
          content_type: 'content',
          sub_category_path: categoryInfo?.value?.path || 'x',
          article_id: articleId.value || 'x',
          video_id: articleId.value || 'x'
        },
        // 视频时长（秒），GAM 依据此参数返回 mid-roll 广告（TC-1596）
        videoDuration: articleVideo.value?.video_time || 0
      })
      if (mrAdTagUrl) {
        // SDK 收到 mr 的 VMAP 后自动跟踪内容进度，在 mid-roll 时间点播放
        // 不需要手动 pause()，SDK 自动管理内容暂停/恢复
        tvbPlayerRef.value?.requestAds(mrAdTagUrl)
      }
    }
  } catch (error) {
    console.error('Failed to fetch mr ad config:', error)
  }
}

// Instream 广告事件处理
const onAdStart = () => {
  console.log('[Article InstreamAd] 广告开始播放')
}
const onAdEnd = () => {
  // pr 广告结束后请求 mr；mr 结束后不做任何事，视频自动恢复播放
  if (currentAdType.value === 'pr') {
    currentAdType.value = 'mr'
    fetchMrAdConfig()
  }
}
const onAdError = (error) => {
  console.warn('[Article InstreamAd] 广告加载失败:', error)
}
const onContentResume = () => {
  console.log('[Article InstreamAd] 内容恢复播放')
}

// 处理点赞
const onClickLike = async () => {
  try {
    const res = await postArticleLike({
      type: 1,
      liked_id: articleId.value,
      status: is_like.value ? 2 : 1
    })
    if (res.code === 150001) {
      loginRef.value?.login && loginRef.value.login()
      throw new Error(res.message)
    }
    if (res.code !== 0) throw new Error(res.message)
    is_like.value = !is_like.value
    like_num.value = is_like.value ? like_num.value + 1 : like_num.value - 1
  } catch (error) {
    console.warn(error)
  }
}

// 交互
const dividerRef = ref()
const targetIsVisible = useElementVisibility(dividerRef)
const relationRef = ref(null)
// Relation 组件根元素有 v-if，初始为空时 $el 为 Comment 节点
// 需要 instanceof Element 检查，避免 ResizeObserver.observe(Comment) 报错
const { height: relationHeight } = useElementSize(
  computed(() => {
    const el = relationRef.value?.$el
    return el instanceof Element ? el : null
  })
)
const asideRef = ref(null)
const { width: asideWidth } = useElementSize(computed(() => asideRef.value))
// - 封面图预览
const richRef = ref()
const handleViewer = (src) => {
  richRef.value?.showViewer?.(src)
}
const relationFixed = ref(false)
const onAffixChange = (e) => (relationFixed.value = e)
// 相关文章模块的 affix 实例引用，用于广告加载后主动触发重新定位
const affixRef = ref(null)
/**
 * 广告加载完成后，主动通知 TvbAffix 重新计算定位
 * 解决广告异步加载导致布局变化时，相关文章模块未及时刷新定位而遮挡广告位的问题
 * 使用 nextTick 等待 Vue 完成 DOM 更新与浏览器 reflow 后再触发
 */
const onAdLoad = () => {
  nextTick(() => {
    affixRef.value?.updateRoot()
    affixRef.value?.update()
  })
}

// seo - start
const VITE_HOST_URL = import.meta.env.VITE_HOST_URL
const canonical = computed(() => VITE_HOST_URL + (activeArticle?.value?.uri?.slice(1) || ''))
const seoTitle = computed(
  () => (activeArticle.value?.seo_title || activeArticle.value?.title) + seoTitleSuffix
)
const seoAuthor = computed(() => activeArticle.value?.author?.join(',') || 'TVB')
const seoImageUrl = computed(
  () => activeArticle.value?.og_image_info?.large || activeArticle.value?.image_info?.large || ''
)
const seoImageAlt = computed(() => activeArticle.value?.og_image_alt_txt || '')
const seoDescription = computed(() => activeArticle.value?.meta_description || '')
const ldJsonScript = computed(() => {
  if (!isActive.value) return ''
  let articleData = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    image: {
      '@type': 'ImageObject',
      contentUrl:
        activeArticle.value?.og_image_info?.large || activeArticle.value?.image_info?.large || '',
      creditText: 'TVB 無綫集團',
      creator: {
        '@type': 'Organization',
        name: 'TVB 無綫集團'
      }
    },
    headline: activeArticle.value?.title,
    datePublished: toISOString(activeArticle.value?.publish_at),
    dateModified: toISOString(activeArticle.value?.updated_at),
    isAccessibleForFree: 'true',
    author: {
      '@type': 'Organization',
      name: 'TVB 無綫集團',
      url: VITE_HOST_URL
    },
    publisher: {
      name: 'TVB 無綫集團',
      url: VITE_HOST_URL
    },
    mainEntityOfPage: canonical.value,
    description: activeArticle.value?.meta_description,
    articleSection: [categoryInfo.value?.parent?.title, categoryInfo.value?.title]
  }
  let keywords = []
  if (activeArticle.value?.tags) {
    keywords = activeArticle.value?.tags.reduce((p, c) => {
      p.push(c.name)
      return p
    }, [])
  }
  articleData.keywords = keywords // 沒有標籤都要顯示keywords
  if (author.value) {
    articleData.author = author.value?.split('、')?.reduce((p, c) => {
      p.push({ name: c })
      return p
    }, [])
  }

  const BreadcrumbList = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      ...breadcrumbs.value.map((v, i) => {
        return {
          '@type': 'ListItem',
          position: i + 1,
          name: v.name,
          item: v.path == '/' ? VITE_HOST_URL : v.path
        }
      }),
      {
        '@type': 'ListItem',
        position: 4,
        name: activeArticle.value?.title,
        item: canonical.value
      }
    ]
  }
  return JSON.stringify([articleData, BreadcrumbList])
})
const extraLdJsonScript = computed(() => {
  const ldJson = activeArticle.value?.ld_json
  if (!ldJson) return ''
  if (typeof ldJson === 'string') {
    const trimLdJson = ldJson.trim()
    if (!trimLdJson) return ''
    try {
      JSON.parse(trimLdJson)
      return trimLdJson
    } catch (error) {
      return ''
    }
  }
  try {
    return JSON.stringify(ldJson)
  } catch (error) {
    return ''
  }
})
const scriptData = computed(() => {
  const script = [
    { id: 'article-ld-json', innerHTML: ldJsonScript.value, type: 'application/ld+json' }
  ]
  if (extraLdJsonScript.value) {
    script.push({
      id: 'article-extra-ld-json',
      innerHTML: extraLdJsonScript.value,
      type: 'application/ld+json'
    })
  }
  return {
    link: [
      {
        rel: 'canonical',
        href: canonical.value
      },
      {
        rel: 'alternate',
        hreflang: 'x-default',
        href: canonical.value
      },
      {
        rel: 'alternate',
        hreflang: 'zh-hk',
        href: canonical.value
      }
    ],
    script
  }
})
const { patch } = useHead(scriptData)

const setSeo = () => {
  if (isPreview.value) {
    return
  }
  // 注入文章详情页广告参数，包含 video_id 确保广告请求携带该字段
  setAdParams({
    content_type: 'content',
    vtype: articleVideo.value.video_url ? 'vod' : 'x',
    article_id: props.info.id || 'x',
    sub_category_path: categoryInfo.value?.path || 'x',
    video_id: articleId.value || 'x'
  })
  if (!import.meta.env.SSR && canonical.value) {
    // TC-1631: 保留 utm 参数在地址栏，canonical link 仍使用干净 URL
    window.history.replaceState(history.state, null, appendUtmParams(canonical.value))
  }
  if (isActive.value) {
    patch(scriptData.value)
  }
}

watch(
  () => [isActive.value, cloading.value],
  () => {
    if (!cloading.value) nextTick(() => setSeo())
  },
  { immediate: true }
)

watch(
  cloading,
  () => {
    if (!cloading.value) {
      if (isActive.value) {
        // 直接打开图片查看器
        setTimeout(() => {
          let photoId = route.query?.photo_id
            ? route.query?.photo_id
            : route.hash && route.hash.split('#photo_id=')?.[1]
          if (photoId) {
            const richTextImgs = richRef.value?.richTextImgs || []
            const hashImgs = richTextImgs.map((v) => sha1(v.src))
            const findIndex = hashImgs.findIndex((v) => v === photoId)
            if (findIndex > -1) {
              richRef.value?.showViewer?.(null, findIndex)
            }
          }
        }, 300)
      }

      pageview({
        ...layerData.value,
        page_title: 'article',
        screen_class: `${categoryInfo.value.parent.title}/${categoryInfo.value.title}/${props.info.title}`,
        eventCategory: 'sub_category',
        eventLabel: 'article',
        eventID: categoryInfo.value.id || '',
        screenLabel: canonical.value
      })
    }
  },
  { flush: 'post' }
)

const layerData = inject('layerData')
const onViewerShown = (pswp) => {
  const index = pswp?.currIndex
  popUp({
    ...layerData.value,
    eventID: 'o003',
    page_title: 'popUp',
    eventLabel: `${categoryInfo.value.title || ''}_popup`,
    screen_class: props.info?.title || '',
    eventCategoy: 'sub_category',
    screenLabel: canonical.value,
    listName: index + 1
  })
  nextTick(() => {
    const slide = pswp?.currSlide
    pageview({
      screenID: 'S006',
      page_title: 'album_view',
      eventLabel: 'album_view',
      screen_class: props.info.title || '',
      eventCategoy: 'sub_category',
      eventID: categoryInfo.value.id || '',
      screenLabel: canonical.value + '?' + sha1(slide?.data.src),
      photo_id: sha1(slide?.data.src)
    })
  })
}

const onViewerChange = (pswp) => {
  action({
    ...layerData.value,
    eventID: 'B021',
    page_title: 'album_popup',
    eventLabel: 'album_popup',
    screen_class: props.info?.title || '',
    eventCategoy: 'sub_category',
    screenLabel: canonical.value,
    photo_id: sha1(pswp?.currSlide?.data.src)
  })
}

const onCopyHandler = function (event) {
  contentCopyHandler(config.article_copyright_text, `${location.origin}/${articleId.value}`, event)
}

onBeforeUnmount(() => {
  draggableIntersectionObserver.stop()
})

defineExpose({ pause, play, elementBounding })
</script>

<template>
  <Head>
    <title>{{ seoTitle }}</title>
    <meta name="author" :content="seoAuthor" />
    <meta name="description" :content="seoDescription" />
    <meta property="og:type" content="article" />
    <meta property="og:image" :content="seoImageUrl" />
    <meta property="og:title" :content="seoTitle" />
    <meta property="og:image:alt" :content="seoImageAlt" />
    <meta property="og:url" :content="canonical" />
    <meta property="og:description" :content="seoDescription" />
    <meta name="twitter:title" :content="seoTitle" />
    <meta name="twitter:image" :content="seoImageUrl" />
    <meta name="twitter:image:alt" :content="seoImageAlt" />
    <meta name="twitter:url" :content="canonical" />
    <meta property="twitter:description" :content="seoDescription" />
  </Head>
  <div
    @copy="onCopyHandler"
    class="tvb-article"
    :class="size ? 'article-' + size : ''"
    ref="articleRef"
  >
    <!-- Top Banner 广告 - 仅第一篇文章面包屑前显示 -->
    <tvb-ads
      v-if="isFirstArticle && categoryInfo?.path && articleId && isShowAd !== 2"
      style="max-width: 848px; margin-bottom: 12px"
      ad-type="topbn"
      :position="9"
      :unit="`/${articleId}`"
      :show-ad="isShowAd !== 2"
      :params="{
        content_type: 'content',
        vtype: articleVideo.video_url ? 'vod' : 'x',
        article_id: articleId || 'x',
        sub_category_path: categoryInfo?.path || 'x',
        video_id: articleId || 'x'
      }"
      @load="onAdLoad"
    />

    <div
      v-show="categoryInfo && !cloading"
      class="tvb-article-breadcrumb"
      :class="{ 'tvb-preview relative': isPreview }"
    >
      <tvb-breadcrumb :breadcrumbs="breadcrumbs" :size="size" :loading="cloading"></tvb-breadcrumb>
    </div>

    <div class="tvb-article-wrap" :class="'affix-container-wrap-' + info.id">
      <article class="tvb-main-article" :class="'article-' + info.id">
        <h1 class="clear-h tvb-article-title" :title="info.title">{{ info.title }}</h1>
        <template v-if="!isMobile && !isPreview">
          <left-tool
            :share-id="info.id"
            :size="size"
            :is-like="is_like"
            :like-num="like_num"
            @like="onClickLike"
          ></left-tool>
        </template>
        <subtitle :info="info" :size="size" />

        <div v-if="articleVideo.video_url" class="tvb-article-video-contain tvb-article-video_box">
          <div class="tvb-article-video-contain-img">
            <img :src="info.image_info.large" style="width: 100%; height: 100%" />
          </div>
          <span ref="draggableRef"></span>
          <tvb-draggable :disabled="draggableDisabled">
            <div
              class="tvb-article-video_box tvb-hairline--surround"
              :class="{ draggable: !draggableDisabled, mobile: isMobile }"
            >
              <tvb-player
                ref="tvbPlayerRef"
                class="tvb-article-video_player"
                :src="articleVideo.video_url"
                :type="getTvbPlayerType(articleVideo?.video_type)"
                :content-id="articleVideo?.content_id"
                :region-only="articleVideo?.region_only"
                :posterUrl="info?.image_info?.large"
                :title="info?.title"
                :video-clarity="articleVideo?.video_clarity"
                :subtitle="articleVideo?.subtitle"
                :ad-tag-url="articleAdTagUrl"
                :show-instream-ads="showInstreamAds"
                :youbora-options="{
                  videoTitle: info?.title,
                  programTitle: '',
                  videoSrc: articleVideo?.video_url,
                  contentId: articleVideo?.video_id,
                  articleId: info?.id
                }"
                mute
                @play-click="
                  action({
                    ...layerData.value,
                    eventID: 'B013',
                    page_title: props.info?.title || '',
                    eventLabel: categoryInfo?.path,
                    screen_class: props.info?.title || '',
                    eventCategoy: 'sub_category',
                    screenLabel: articleVideo.video_url || '',
                    listName: 'video'
                  })
                "
                @stateChange="onStateChange"
                @ad-start="onAdStart"
                @ad-end="onAdEnd"
                @ad-error="onAdError"
                @content-resume="onContentResume"
              />
              <tvb-image
                class="tvb-article-video_image"
                :src="info.image_info"
                :alt="info.title"
              ></tvb-image>
            </div>
          </tvb-draggable>
        </div>
        <div
          v-else
          class="tvb-article-cover_box tvb-hairline--surround"
          @click="handleViewer(info.image_info?.large)"
        >
          <tvb-image :src="info.image_info" :alt="info.title"></tvb-image>
        </div>

        <!-- 展示型广告（os 类型）- 根据 is_show_ad 控制显示 -->
        <tvb-ads
          v-if="categoryInfo?.path && articleId && isShowAd !== 2"
          ad-type="os"
          :position="9"
          :unit="`/${articleId}`"
          :show-ad="isShowAd !== 2"
          :params="{
            content_type: 'content',
            vtype: articleVideo.video_url ? 'vod' : 'x',
            article_id: articleId || 'x',
            sub_category_path: categoryInfo?.path || 'x',
            video_id: articleId || 'x'
          }"
          @load="onAdLoad"
        />

        <div class="tvb-article-content">
          <article-content
            v-if="!eloading && contents"
            ref="richRef"
            :content="contents"
            :id="articleId"
            :img="articleVideo.video_url ? '' : info.image_info?.large"
            :thumbnail="info.image_info?.small"
            @viewerShown="onViewerShown"
            @viewerChange="onViewerChange"
          ></article-content>
        </div>

        <div
          v-if="info.tags.length > 0"
          class="tvb-article-tags"
          :class="{ 'tvb-preview': isPreview, 'tvb-article-tags_hover': !isMobile }"
        >
          <template v-for="item in info.tags" :key="item.id">
            <tvb-router-link
              v-if="item.name"
              class="tvb-article-tag tvb-hairline--surround"
              :to="`/search?tag=${item.id}&tag_name=${item.name}`"
            >
              <h6 class="clear-h tvb-article-tag_inner">#{{ item.name }}</h6>
            </tvb-router-link>
          </template>
        </div>

        <div ref="dividerRef" class="tvb-article-divider"></div>
        <!-- 展示型广告（sb/bn 类型）- 文章下方分隔线下方 -->
        <!-- >= 768px 使用 Super Banner (sb)，< 768px 使用 Banner (bn) -->
        <tvb-ads
          v-if="categoryInfo?.path && articleId && isShowAd !== 2"
          style="max-width: 848px; margin-top: 12px"
          :ad-type="width >= 768 ? 'sb' : 'bn'"
          :key="'bottom-' + (width >= 768 ? 'sb' : 'bn')"
          :position="9"
          :unit="`/${articleId}`"
          :show-ad="isShowAd !== 2"
          :params="{
            content_type: 'content',
            vtype: articleVideo.video_url ? 'vod' : 'x',
            article_id: articleId || 'x',
            sub_category_path: categoryInfo?.path || 'x',
            video_id: articleId || 'x'
          }"
          @load="onAdLoad"
        />
      </article>

      <transition name="tvb-fade-out">
        <div
          v-if="showRelation && !isPreview"
          ref="asideRef"
          class="tvb-article-relation-container"
        >
          <tvb-affix
            ref="affixRef"
            :target="'.affix-container-wrap-' + info.id"
            :offset="96"
            zIndex="9"
            :rootSize="{
              rootHeight: relationHeight || null,
              rootWidth: asideWidth || null
            }"
            @change="onAffixChange"
          >
            <relation ref="relationRef" :id="articleId" :fixed="relationFixed" />
          </tvb-affix>
        </div>
      </transition>
    </div>

    <!-- Takeover 广告 - 文章详情页 -->
    <tvb-ads
      v-if="isFirstArticle && categoryInfo?.path && articleId && isShowAd !== 2"
      ad-type="to"
      :position="9"
      :unit="`/${articleId}`"
      :show-ad="isShowAd !== 2"
      :params="{
        content_type: 'content',
        vtype: articleVideo.video_url ? 'vod' : 'x',
        article_id: articleId || 'x',
        sub_category_path: categoryInfo?.path || 'x',
        video_id: articleId || 'x'
      }"
      @load="onAdLoad"
    />

    <transition name="tvb-slide-up">
      <bottom-tool
        v-if="isMobile && delayShow"
        v-show="!targetIsVisible && isActive && elementBounding.bottom.value > 0"
        :share-id="info.id"
        :size="size"
        :is-like="is_like"
        :like-num="like_num"
        :show-btn="!isPreview"
        @like="onClickLike"
      ></bottom-tool>
    </transition>

    <tvb-sso-button type="login-logout" ref="loginRef" style="display: none" />
  </div>
</template>

<style lang="scss" scoped>
.tvb-article {
  --tvb-article-spacing: 32px;
  position: relative;

  :deep(.tvb-ads) {
    max-width: 100% !important;
  }

  & + .tvb-article {
    margin-top: var(--tvb-article-spacing);
  }

  .tvb-article-divider {
    height: 1px;
    background: rgba(163, 178, 188, 0.35);
  }

  .tvb-article-breadcrumb {
    margin-bottom: 32px;
  }

  .tvb-article-wrap {
    position: relative;
    display: flex;
  }

  // 主体文章内容
  .tvb-main-article {
    width: 100%;
    max-width: 848px;
    position: relative;

    .tvb-article-title {
      font-size: 28px;
      font-weight: 500;
      color: #1c191a;
      line-height: 40px;
      letter-spacing: 0.5px;
      margin-bottom: 8px;
    }

    .tvb-article-author {
      margin-bottom: 2px;
    }

    .tvb-article-date {
      display: flex;
      flex-wrap: wrap;
      .tvb-article-date_publish {
        margin-right: 12px;
      }
    }

    .tvb-article-cover_box,
    .tvb-article-video_box {
      aspect-ratio: 16 / 9;
      // border-radius: 8px;
      // --tvb-hairline-border-radius: calc(2 * 8px);
      // @include safari-overflow-hidden();
      overflow: hidden;
      // margin-bottom: 32px;
    }
    .tvb-article-cover_box {
      position: relative;
      cursor: pointer;
      .viewer-placeholder {
        position: absolute;
        visibility: hidden;
      }
      @supports not (aspect-ratio: 16 / 9) {
        padding-top: 56.25%;
        .tvb-image,
        .viewer-placeholder {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
        }
      }
    }
    .tvb-article-video_box {
      &.draggable {
        width: 400px;
        height: 225px;
        box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.16);

        &.mobile {
          width: 200px;
          height: 112px;
          box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.12);
        }
      }
      .tvb-article-video_player {
        width: 100%;
        height: 100%;
        z-index: 1;
      }
      .tvb-article-video_image {
        position: absolute;
        top: 0px;
        left: 0px;
        z-index: 0;
        // border-radius: 8px;
        // @include safari-overflow-hidden();
        // overflow: hidden;
      }
    }

    .tvb-article-content {
      margin-bottom: 32px;
      position: relative;
    }

    // 标签样式
    .tvb-article-tags {
      margin-bottom: 32px;
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      position: relative;
      a {
        text-decoration: none;
        background-color: transparent;
        color: inherit;
        transition: color 0.15s linear;
        cursor: pointer;
        &:visited {
          color: inherit;
        }
      }
      .tvb-article-tag {
        --tvb-article-tag-color: #555555;
        --tvb-article-tag-bgc: #f8f9fa;
        --tvb-article-tag-fs: 14px;
        --tvb-article-tag-lh: 20px;
        --tvb-article-tag-ls: 0.3px;
        box-sizing: border-box;
        max-width: 100%;
        height: 32px;
        border-radius: 16px;
        --tvb-hairline-border-radius: calc(2 * 16px);
        background-color: var(--tvb-article-tag-bgc);
        padding: 0 12px;
        display: inline-flex;
        justify-content: center;
        align-items: center;
        transition: background-color 0.15s linear;
        .tvb-article-tag_inner {
          color: var(--tvb-article-tag-color);
          font-size: var(--tvb-article-tag-fs);
          letter-spacing: var(--tvb-article-tag-ls);
          line-height: var(--tvb-article-tag-lh);
          @include text-ellipsis();
        }
      }

      &.tvb-article-tags_hover .tvb-article-tag:hover {
        --tvb-article-tag-color: #0062a3;
        --tvb-article-tag-bgc: #fff;
      }
    }

    .tvb-article-video-contain {
      width: 100%;
      position: relative;

      .tvb-article-video-contain-img {
        width: 100%;
        height: 100%;
        filter: blur(10px);
        position: absolute;
        background: #000;
      }
    }
  }

  &.article-s,
  &.article-xs {
    --tvb-article-spacing: 22px;
    .tvb-article-breadcrumb {
      margin-bottom: 16px;
    }

    .tvb-main-article {
      .tvb-article-title {
        font-size: 22px;
        line-height: 32px;
        margin-bottom: 6px;
        color: #1c191a;
      }

      .tvb-article-date_publish {
        margin-right: 8px;
      }

      .tvb-article-cover_box,
      .tvb-article-video_box {
        // border-radius: 6px;
        // --tvb-hairline-border-radius: calc(2 * 6px);
        // margin-bottom: 22px;
      }

      .tvb-article-content {
        margin-bottom: 22px;
      }

      .tvb-article-tags {
        margin-bottom: 22px;
        gap: 10px;
        .tvb-article-tag {
          --tvb-article-tag-fs: 15px;
          --tvb-article-tag-lh: 22px;
          --tvb-article-tag-ls: 0px;
        }
      }
    }
  }

  // 相關文章
  .tvb-article-relation-container {
    margin-left: 32px;
    flex: 1;
    min-width: 360px;
  }
}
</style>
